import { Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { FC, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  _deleteAdminInvitation,
  _resendAdminInvitation,
  revocableInvitationTypes,
} from '@/api/admin/mutations';
import { ADMIN_VIEWS_QUERY_KEY } from '@/api/admin/queries';
import { AdminDashboardInvitationType, AdminDashboardUserStatus } from '@/api/admin/types';
import { _blockUser, _unblockUser } from '@/api/user/mutations';
import { useGetUserProfileSuspense } from '@/api/user-dashboard/queries';
import { queryClient } from '@/app/providers/react-query-provider';
import DeleteIcon from '@/assets/svg/ui-elements/delete.svg';
import DeleteFilledIcon from '@/assets/svg/ui-elements/delete-filled-red.svg';
import LockIcon from '@/assets/svg/ui-elements/lock-alt.svg';
import MailIcon from '@/assets/svg/ui-elements/mail.svg';
import SendIcon from '@/assets/svg/ui-elements/send-alt.svg';
import { IconButton, Tooltip } from '@/design-system';
import { colors } from '@/design-system/colors';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { getCommunicationContactPath } from '@/pages/communications-v2/utils/get-communication-contact-path';
import { Permission, useCan } from '@/shared/modules/permissions';

import { UserTabTableRow } from './user-tab-table.utils';

type Props = Pick<
  UserTabTableRow,
  | 'statuses'
  | 'email'
  | 'id'
  | 'nameWithAlias'
  | 'isGammaTeam'
  | 'invitationType'
  | 'invitationTypes'
>;

export const UserTabTableActions: FC<Props> = ({
  id,
  email,
  statuses,
  isGammaTeam,
  invitationType,
  invitationTypes,
}) => {
  const isPending = statuses.includes(AdminDashboardUserStatus.Pending);
  const isBlocked = statuses.includes(AdminDashboardUserStatus.Blocked);
  const navigate = useNavigate();

  const { user: meData } = useGetUserProfileSuspense();

  const { mutate: resendInvitation, isPending: isResendInvitationPending } = useMutation({
    mutationFn: _resendAdminInvitation,
  });

  const onMutationConfirmInvalidation = async () => {
    await queryClient.invalidateQueries({
      queryKey: ADMIN_VIEWS_QUERY_KEY.usersRoot,
    });
  };

  const { mutateAsync: blockUserAsync, isPending: isBlockUserPending } = useMutation({
    mutationFn: _blockUser,
  });

  const { handleOpen: openBlockModal, handleClose: closeBlockModal } = useGlobalModal(
    'BLOCK_ACTION_MODAL',
    {
      type: 'user',
      isLoading: isBlockUserPending,
      onConfirm: async (reasons) => {
        await blockUserAsync({
          userId: id,
          blockedBy: meData.id,
          ...reasons,
        });
        await onMutationConfirmInvalidation();
        closeBlockModal();
      },
    },
  );

  const { mutateAsync: unblockUserAsync, isPending: isUnblockUserPending } = useMutation({
    mutationFn: _unblockUser,
  });

  const { handleOpen: openUnblockModal, handleClose: closeUnblockModal } = useGlobalModal(
    'CONFIRM_ACTION_MODAL',
    {
      modalTitle: 'Unblock User',
      btnLabel: 'Unblock',
      confirmationTitle: 'Are you sure you want to Unblock this user?',
      icon: <LockIcon width={24} height={24} color={colors.orange.solid} />,
      isLoading: isUnblockUserPending,
      onConfirm: async () => {
        await unblockUserAsync({
          userId: id,
          unblockedBy: meData.id,
        });
        await onMutationConfirmInvalidation();
        closeUnblockModal();
      },
      btnColor: 'orange',
    },
  );

  const onContact = (e: MouseEvent) => {
    e.stopPropagation();
    navigate(getCommunicationContactPath({ recipientType: 'User', recipientId: id }));
  };
  const onResendInvitation = (e: MouseEvent) => {
    e.stopPropagation();
    if (!invitationType) return;
    resendInvitation({ email, invitationType });
  };
  // `BlockUserHandler` and `UnblockUserHandler` both gate on `UserSuspend`; both
  // additionally require `GammaTeamAssignSuperAdmin` when the target is Gamma staff.
  const canSuspend = useCan(Permission.UserSuspend);
  const canActOnGammaTeam = useCan(Permission.GammaTeamAssignSuperAdmin);
  const canResendInvitations = useCan(Permission.ResendInvitations);
  const isBlockDisabled = !canSuspend || (isGammaTeam && !canActOnGammaTeam);

  const onBlockOrUnblock = (e: MouseEvent) => {
    e.stopPropagation();
    if (isBlocked) {
      openUnblockModal();
    } else {
      openBlockModal();
    }
  };

  const canRevokeGammaTeam = useCan(Permission.GammaTeamManage);
  const canRevokeEntity = useCan(Permission.ResendInvitations);
  const revocableTypes = revocableInvitationTypes(invitationTypes);
  const canRevokeEveryPlane = revocableTypes.every((type) =>
    type === AdminDashboardInvitationType.GammaTeam ? canRevokeGammaTeam : canRevokeEntity,
  );
  const canRevokeInvitation = isPending && revocableTypes.length > 0 && canRevokeEveryPlane;

  const { mutateAsync: deleteInvitationAsync, isPending: isDeleteInvitationPending } = useMutation({
    mutationFn: _deleteAdminInvitation,
  });

  const { handleOpen: openDeleteModal, handleClose: closeDeleteModal } = useGlobalModal(
    'CONFIRM_ACTION_MODAL',
    {
      modalTitle: 'Delete Invitation',
      btnLabel: 'Delete',
      confirmationTitle: 'Are you sure you want to delete this invitation?',
      confirmationDescription:
        'This revokes the invitation. The invitee will no longer be able to join from it.',
      icon: <DeleteFilledIcon width={24} height={24} />,
      isLoading: isDeleteInvitationPending,
      onConfirm: async () => {
        await deleteInvitationAsync({ email, invitationTypes });
        await onMutationConfirmInvalidation();
        closeDeleteModal();
      },
      btnColor: 'red',
    },
  );

  const onDeleteInvitation = (e: MouseEvent) => {
    e.stopPropagation();
    openDeleteModal();
  };

  const deleteTooltip = !isPending
    ? 'This user is already registered and cannot be deleted.'
    : invitationTypes.length === 0
      ? 'Unknown invitation type'
      : revocableTypes.length === 0
        ? 'This shareholder invitation is associated with an entity and cannot be deleted.'
        : !canRevokeEveryPlane
          ? 'You do not have permission to delete this invitation'
          : 'Delete Invitation';

  return (
    <Stack direction="row" alignItems="center" gap="8px">
      <Tooltip title="Contact">
        <span>
          <IconButton
            aria-label="Contact user"
            color="tertiary"
            variant="outline"
            size="xs"
            disabled={isBlocked || !id}
            onClick={onContact}
          >
            <MailIcon />
          </IconButton>
        </span>
      </Tooltip>

      {isPending ? (
        <Tooltip
          title={
            !canResendInvitations
              ? 'You do not have permission to resend invitations'
              : invitationType
                ? 'Resend Invite'
                : 'Unknown invitation type'
          }
        >
          <span>
            <IconButton
              aria-label="Resend invite"
              color="tertiary"
              variant="outline"
              size="xs"
              disabled={!canResendInvitations || isResendInvitationPending || !invitationType}
              onClick={onResendInvitation}
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip
          title={
            isBlockDisabled
              ? 'You do not have permission to block this user'
              : isBlocked
                ? 'Unblock'
                : 'Block'
          }
        >
          <span>
            <IconButton
              aria-label={isBlocked ? 'Unblock user' : 'Block user'}
              color={isBlocked ? 'secondary' : 'destructive'}
              variant="solid"
              size="xs"
              disabled={isBlockDisabled}
              onClick={onBlockOrUnblock}
            >
              <LockIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}

      <Tooltip title={deleteTooltip}>
        <span>
          <IconButton
            aria-label={isPending ? 'Delete invitation' : 'Delete user'}
            color="destructive"
            variant="solid"
            size="xs"
            disabled={!canRevokeInvitation || isDeleteInvitationPending}
            onClick={onDeleteInvitation}
          >
            <DeleteIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};
